/**
 * Authentication Context
 * Manages user authentication state and provides auth methods
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api';

/**
 * Decode a JWT token payload without verifying signature (client-side only)
 * Used to read isSuperadmin and other claims from the stored access token
 */
function decodeJwtPayload(token) {
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [currentTenant, setCurrentTenant] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in on mount
    const accessToken = localStorage.getItem('accessToken');
    const storedUser = localStorage.getItem('user');
    const storedTenantId = localStorage.getItem('tenantId');

    if (accessToken && storedUser) {
      try {
        // Prevent parsing invalid string values
        if (storedUser === 'undefined' || storedUser === 'null' || storedUser === '') {
          console.warn('Invalid stored user value detected, clearing auth state');
          localStorage.removeItem('user');
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('tenantId');
          setLoading(false);
          return;
        }

        const parsedUser = JSON.parse(storedUser);

        // Merge isSuperadmin from the JWT payload in case the stored user
        // object is stale (logged in before the superadmin feature was deployed)
        if (!parsedUser.isSuperadmin) {
          const jwtPayload = decodeJwtPayload(accessToken);
          if (jwtPayload?.isSuperadmin) {
            parsedUser.isSuperadmin = true;
          }
        }

        setUser(parsedUser);

        if (storedTenantId) {
          // TODO: Fetch full tenant details if needed
          setCurrentTenant({ id: parseInt(storedTenantId) });
        }
      } catch (error) {
        console.error('Error parsing stored user:', error);
        // Clear invalid data
        localStorage.removeItem('user');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('tenantId');
      }
    }

    setLoading(false);

    // Listen for auth failures from API interceptor
    const handleAuthFailure = () => {
      console.log('Auth failure detected, clearing user state');
      setUser(null);
      setTenants([]);
      setCurrentTenant(null);
    };

    window.addEventListener('auth:logout', handleAuthFailure);

    return () => {
      window.removeEventListener('auth:logout', handleAuthFailure);
    };
  }, []);

  /**
   * Login user with email and password
   * @param {string} email
   * @param {string} password
   * @returns {Promise} Login response data
   */
  const login = async (email, password) => {
    const { data } = await authAPI.login({ email, password });

    setUser(data.user);
    setTenants(data.tenants);

    // Store tokens and user data
    localStorage.setItem('accessToken', data.accessToken);
    localStorage.setItem('refreshToken', data.refreshToken);

    // Validate user data before storing
    if (data && data.user) {
      localStorage.setItem('user', JSON.stringify(data.user));
    } else {
      console.error('Login response missing user object:', data);
      throw new Error('Invalid login response: user data missing');
    }

    // Set first tenant as default
    if (data.tenants.length > 0) {
      const firstTenant = data.tenants[0];
      setCurrentTenant(firstTenant);
      localStorage.setItem('tenantId', firstTenant.id.toString());
    }

    return data;
  };

  /**
   * Register new user and create tenant
   * @param {Object} formData - Registration form data
   * @returns {Promise} Registration response data
   */
  const register = async (formData) => {
    const { data } = await authAPI.register(formData);

    setUser(data.user);
    setCurrentTenant(data.tenant);
    setTenants([data.tenant]); // User becomes owner of new tenant

    // Store tokens and user data
    localStorage.setItem('accessToken', data.accessToken);
    localStorage.setItem('refreshToken', data.refreshToken);

    // Validate user data before storing
    if (data && data.user) {
      localStorage.setItem('user', JSON.stringify(data.user));
    } else {
      console.error('Register response missing user object:', data);
      throw new Error('Invalid registration response: user data missing');
    }

    localStorage.setItem('tenantId', data.tenant.id.toString());

    return data;
  };

  /**
   * Logout user
   */
  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear state and localStorage
      setUser(null);
      setTenants([]);
      setCurrentTenant(null);
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      localStorage.removeItem('tenantId');
      // Clear any impersonation state
      localStorage.removeItem('impersonationActive');
      localStorage.removeItem('impersonatedTenantName');
      localStorage.removeItem('superadminToken');
      localStorage.removeItem('superadminRefreshToken');
    }
  };

  /**
   * Switch between tenants (for users with multiple tenants)
   * @param {Object} tenant - Tenant to switch to
   */
  const switchTenant = (tenant) => {
    setCurrentTenant(tenant);
    localStorage.setItem('tenantId', tenant.id.toString());
  };

  const value = {
    user,
    tenants,
    currentTenant,
    loading,
    login,
    register,
    logout,
    switchTenant,
    isAuthenticated: !!user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook to use auth context
 * Must be used within AuthProvider
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
